import {Pattern, match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {
  ASSISTANT_INTEREST_PLANNED_ACTIVITIES_MAX_ITEMS,
  ASSISTANT_INTEREST_TIMELINE_MAX_EVENTS,
  type InterestFacts,
  type InterestPlannedActivity,
} from '@prompt-bridge/shared/ai-assistant-core';

import type {ToSalesInterestFactsInput} from '../types/ToSalesInterestFactsInput';
import {compareInterestPlannedActivities} from './compareInterestPlannedActivities';
import {compareInterestTimelineEventsNewestFirst} from './compareInterestTimelineEventsNewestFirst';
import {mapSalesActivityToTimelineEvent} from './mapSalesActivityToTimelineEvent';
import {toFallbackTestDrive} from './toFallbackTestDrive';
import {toInterestLeadQuality} from './toInterestLeadQuality';
import {toInterestLifecycleState} from './toInterestLifecycleState';
import {toInterestMessageTimelineEvent} from './toInterestMessageTimelineEvent';
import {toTaskInterestPlannedActivity} from './toTaskInterestPlannedActivity';
import {toUpcomingTestDrivePlannedActivities} from './toUpcomingTestDrivePlannedActivities';

const toTestDriveActivities = (
  input: ToSalesInterestFactsInput,
  timeline: InterestFacts['timeline']
): InterestPlannedActivity[] => {
  const fallbackTestDrive = toFallbackTestDrive(input);
  const fallbackTestDriveActivities = isNotNil(fallbackTestDrive) ? [fallbackTestDrive] : [];
  const activityTestDriveActivities = match(input.activities)
    .with(Pattern.nonNullable, (activities) => {
      const testDriveByTimelineEventId = new Map(
        activities.flatMap((activity) =>
          isNotNil(activity.relatedTestDrive)
            ? [[activity.id, activity.relatedTestDrive] as const]
            : []
        )
      );

      // Activity history is cancellation-aware: only the latest normalised timeline event for
      // each future test drive can survive. The minipurchase date can retain a cancelled visit.
      return toUpcomingTestDrivePlannedActivities(
        timeline,
        testDriveByTimelineEventId,
        input.evaluatedAt
      );
    })
    .otherwise(() => []);

  return [...activityTestDriveActivities, ...fallbackTestDriveActivities];
};

export function toSalesInterestFacts(input: ToSalesInterestFactsInput): InterestFacts {
  const sourceActivities = input.activities ?? [];
  const activityMessageIds = new Set(
    sourceActivities.flatMap((activity) =>
      isNotNil(activity.relatedMessage) ? [activity.relatedMessage.id] : []
    )
  );
  const activityCallIds = new Set(
    sourceActivities.flatMap((activity) =>
      isNotNil(activity.relatedCall) ? [activity.relatedCall.id] : []
    )
  );
  const fullTimeline = [
    ...sourceActivities.map((activity) => ({
      id: activity.id,
      occurredAt: activity.timestamp,
      ...mapSalesActivityToTimelineEvent(activity.activityType),
    })),
    // A message the DMS itself sent, or a call it logged, is already an activity; keep the
    // activity as the single event.
    ...(input.customerMessages ?? [])
      .filter(
        (message) =>
          !activityMessageIds.has(message.id) &&
          (isNil(message.call) || !activityCallIds.has(message.call.id))
      )
      .map(toInterestMessageTimelineEvent),
  ].sort(compareInterestTimelineEventsNewestFirst);
  const timeline = fullTimeline.slice(0, ASSISTANT_INTEREST_TIMELINE_MAX_EVENTS);
  const omittedTimelineEventCount = Math.max(0, fullTimeline.length - timeline.length);
  const fullPlannedActivities = [
    ...input.upcomingTasks.map(toTaskInterestPlannedActivity),
    ...toTestDriveActivities(input, fullTimeline),
  ].sort(compareInterestPlannedActivities);
  const plannedActivities = fullPlannedActivities.slice(
    0,
    ASSISTANT_INTEREST_PLANNED_ACTIVITIES_MAX_ITEMS
  );
  const omittedPlannedActivityCount = Math.max(
    0,
    fullPlannedActivities.length - plannedActivities.length
  );

  return {
    kind: 'sales',
    interestId: input.interestId,
    state: toInterestLifecycleState(input.interest.state),
    createdAt: input.interest.createdAt,
    updatedAt: input.interest.updatedAt ?? null,
    firstResponseAt: input.interest.firstResponseAt ?? null,
    leadQuality: toInterestLeadQuality(input.interest.leadQuality),
    timeline,
    plannedActivities,
    completeness: {
      timelineComplete: isNotNil(input.activities) && omittedTimelineEventCount === 0,
      omittedTimelineEventCount,
      plannedActivitiesComplete: isNotNil(input.activities),
      omittedPlannedActivityCount,
      timelineCoveredFrom: timeline.at(-1)?.occurredAt ?? null,
    },
  };
}
